import { addMinutes } from "date-fns";

export const getCurrentTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const getTimeZoneAbbreviation = (timeZone: string, options?: Intl.DateTimeFormatOptions) => {
    // TODO: In the future this may need to be able to handle non-US time zones better.
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortGeneric',
        ...options
    });

    const parts = formatter.formatToParts(new Date());
    const timeZoneAbbreviation = parts.find(part => part.type === 'timeZoneName');

    return timeZoneAbbreviation ? timeZoneAbbreviation.value : null;
}

export const getTimeZoneName = (timeZone: string, date: Date = new Date()): string => {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'longGeneric',
        });

        // Format the date and extract the time zone name
        const parts = formatter.formatToParts(date);
        const timeZonePart = parts.find(part => part.type === 'timeZoneName');

        if (timeZonePart) {
            return timeZonePart.value; // This will return names like "Central Time"
        }

        throw new Error('Time zone name could not be determined');
    } catch (error) {
        throw new Error(`Invalid time zone name: ${timeZone}`);
    }
}

// Returns a mapping of Record<TimeZoneName (Central Time), TimeZone[] ([America/Chicago])>
export const getTimeZoneNameMapping = () => {
    const mapping: Record<string, string[]> = {};
    Intl.supportedValuesOf('timeZone').forEach((timeZone) => {
        const timeZoneName = getTimeZoneName(timeZone);
        if (timeZoneName in mapping) {
            mapping[timeZoneName].push(timeZone);
        } else {
            mapping[timeZoneName] = [timeZone];
        }
    });
    return mapping;
}

// Adjust a date from one time zone to a new time zone
const modifyTimeZone = (date: Date, fromTimeZone: string, toTimeZone: string) => {
    const oldTimeZoneOffset = getTimeZoneOffset(fromTimeZone);
    const newTimeZoneOffset = getTimeZoneOffset(toTimeZone);
    return modifyTimeZoneOffset(date, oldTimeZoneOffset, newTimeZoneOffset);
}

export const getTimeZoneOffset = (timeZone: string): number => {
    const dateString = new Date().toLocaleString('en', { timeZone, timeZoneName: 'longOffset' });
    const [_, hours, minutes] = dateString.match(/([+-]\d+):(\d+)$/) || [, '+00', '00'];
    if (hours === undefined || minutes === undefined) {
        throw new Error("Invalid time zone")
    }
    const parsedHours = parseInt(hours);
    const parsedMinutes = parseInt(minutes);
    const result = 60 * parsedHours + (parsedHours > 0 ? +parsedMinutes : -parsedMinutes)
    return result;
}

// Adjust a date from one time zone offset to a new time zone offset
export const modifyTimeZoneOffset = (date: Date, oldOffset: number, newOffset: number) => {
    const differenceInTimeZoneOffset = newOffset - oldOffset;
    return addMinutes(new Date(date), differenceInTimeZoneOffset);
}

export const convertTimestampToCurrentTimeZone = (timestamp: Date, fromTimeZone: string) => {
    const currentTimeZone = getCurrentTimeZone();
    return modifyTimeZone(timestamp, fromTimeZone, currentTimeZone);
}

export const convertTimestampFromCurrentTimeZoneToTimeZone = (timestamp: Date, timeZone: string) => {
    const currentTimeZone = getCurrentTimeZone();
    return modifyTimeZone(timestamp, currentTimeZone, timeZone);
}

export const tryConvertTimestampFromCurrentTimeZoneToTimeZone = (timestamp: Date, timeZone?: string) => {
    if (timeZone) {
        return convertTimestampFromCurrentTimeZoneToTimeZone(timestamp, timeZone);
    } else {
        return timestamp; // Return unconverted timestamp
    }
}

export const maintainTimeInNewTimeZone = (timestamp: Date, fromTimeZone: string, toTimeZone: string) => {
    const oldTimeZoneOffset = getTimeZoneOffset(fromTimeZone);
    const newTimeZoneOffset = getTimeZoneOffset(toTimeZone);
    const differenceInTimeZoneOffset = oldTimeZoneOffset - newTimeZoneOffset;
    return addMinutes(new Date(timestamp), differenceInTimeZoneOffset);
}